const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const equal = (left, right) => {
  if (left === right) return true;
  if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((item, index) => equal(item, right[index]));
  if (object(left) && object(right)) return Object.keys(left).length === Object.keys(right).length && Object.keys(left).every(key => Object.prototype.hasOwnProperty.call(right, key) && equal(left[key], right[key]));
  return false;
};
// Compare clinical values at the precision clinicians can enter. Blank controls
// and absent optional values mean the same thing; zero and false remain values.
export const clinicalEqual = (left, right, path = '') => {
  const field = path.split('.').pop();
  const blank = value => value == null || value === ''
    || (field === 'apgarScores' && Array.isArray(value) && value.every(item => item == null || item === ''));
  if (blank(left) && blank(right)) return true;
  if (/(date|time|at)$/i.test(field) && typeof left === 'string' && typeof right === 'string') {
    const timestamp = value => {
      const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:\d{2})?$/i);
      if (!match) return value;
      const fraction = (match[4] || '').replace(/0+$/, '');
      const zone = /^(Z|\+00:00)$/i.test(match[5] || '') ? 'Z' : (match[5] || '');
      return `${match[1]}T${match[2]}:${match[3] || '00'}${fraction ? `.${fraction}` : ''}${zone}`;
    };
    return timestamp(left) === timestamp(right);
  }
  const precision = { weightOz: 1, weightLbs: 0, weightKg: 2, weightInGrams: 0, lengthInCm: 1, lengthInches: 1 }[field];
  if (precision !== undefined && !blank(left) && !blank(right)
      && Number.isFinite(Number(left)) && Number.isFinite(Number(right)))
    return Number(left).toFixed(precision) === Number(right).toFixed(precision);
  return equal(left, right);
};
const entryId = value => value?.referenceId ?? value?.ReferenceId ?? value?.clientEntryId ?? value?.id ?? value?.Id;

export function applyClinicalPatch(base, patch) {
  if (!object(patch)) return patch;
  const result = { ...(base || {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (['__proto__', 'constructor', 'prototype'].includes(key) || value === undefined) continue;
    result[key] = object(value) ? applyClinicalPatch(result[key], value) : value;
  }
  return result;
}

export function clinicalChanges(base, desired, path = '') {
  if (!object(desired)) return clinicalEqual(base, desired, path) ? undefined : desired;
  const changed = {};
  for (const key of Object.keys(desired)) {
    if (['__proto__', 'constructor', 'prototype'].includes(key)) continue;
    const value = clinicalChanges(base?.[key], desired[key], path ? `${path}.${key}` : key);
    if (value !== undefined) changed[key] = value;
  }
  return Object.keys(changed).length ? changed : undefined;
}

// A three-way merge keeps local conflicting values visible while returning
// their paths for review. Unkeyed arrays are indivisible; keyed arrays support
// independent appends, edits, and explicit removals without index matching.
export function mergeClinicalValues(base, local, server, path = '') {
  // Audit metadata is assigned by the server, never a competing clinical edit.
  if (path.split('.').pop() === 'createdAt' && server != null) return { value: server, conflicts: [] };
  // Newly created device records omit optional fields that the server returns
  // as null. Neither representation records a value, so this normalization
  // must not conflict with an edit made while creation was in flight.
  if (base == null && local == null) return { value: server, conflicts: [] };
  if (base == null && server == null) return { value: local, conflicts: [] };
  if (clinicalEqual(local, base, path)) return { value: server, conflicts: [] };
  if (clinicalEqual(server, base, path) || clinicalEqual(local, server, path)) return { value: local, conflicts: [] };
  const arrays = [base, local, server];
  if (arrays.every(Array.isArray) && arrays.every(items => items.every(item => entryId(item) != null)
    && new Set(items.map(item => String(entryId(item)))).size === items.length)) {
    const maps = arrays.map(items => new Map(items.map(item => [String(entryId(item)), item])));
    const ids = [...new Set([...server.map(item => String(entryId(item))), ...local.map(item => String(entryId(item)))])];
    const result = { value: [], conflicts: [] };
    for (const id of ids) {
      const merged = mergeClinicalValues(maps[0].get(id), maps[1].get(id), maps[2].get(id), `${path}[${id}]`);
      if (merged.value !== undefined) result.value.push(merged.value);
      result.conflicts.push(...merged.conflicts);
    }
    return result;
  }
  if (object(local) && object(server) && (base == null || object(base))) {
    const result = { value: {}, conflicts: [] };
    // An occurrence and its clock fields are one clinical fact. Conservatively
    // review concurrent timing edits rather than synthesizing a third timestamp.
    const timeKeys = new Set([base, local, server].flatMap(value => Object.keys(value?.clinicalTimeMetadata || {}))
      .flatMap(key => key === 'recordedAt' ? ['date', 'time'] : [key.split('.')[0]]));
    if (timeKeys.size) {
      timeKeys.add('clinicalTimeMetadata');
      const group = value => Object.fromEntries([...timeKeys].map(key => [key, value?.[key] ?? null]));
      const b = group(base), l = group(local), s = group(server);
      const conflict = !equal(l, b) && !equal(s, b) && !equal(l, s);
      const selected = equal(l, b) ? server : local;
      for (const key of timeKeys) {
        if (selected?.[key] !== undefined) result.value[key] = selected[key];
        if (conflict) result.conflicts.push({ path: path ? `${path}.${key}` : key, base: base?.[key], local: local[key], server: server[key] });
      }
    }
    for (const key of new Set([...Object.keys(base || {}), ...Object.keys(local), ...Object.keys(server)])) {
      if (['__proto__', 'constructor', 'prototype'].includes(key)) continue;
      if (timeKeys.has(key)) continue;
      const merged = mergeClinicalValues(base?.[key], local[key], server[key], path ? `${path}.${key}` : key);
      if (merged.value !== undefined) result.value[key] = merged.value;
      result.conflicts.push(...merged.conflicts);
    }
    return result;
  }
  return { value: local, conflicts: [{ path, base, local, server }] };
}

export function withoutConflictedFields(patch, conflicts) {
  if (!patch) return {};
  const copy = JSON.parse(JSON.stringify(patch));
  for (const { path, server } of conflicts) {
    const keys = path.match(/[^.[\]]+/g) || [];
    if (!keys.length) return {};
    let parent = copy;
    for (const key of keys.slice(0, -1)) parent = Array.isArray(parent)
      ? parent.find(item => String(entryId(item)) === key) : parent?.[key];
    if (!parent || typeof parent !== 'object') continue;
    const key = keys[keys.length - 1];
    if (Array.isArray(parent)) {
      const index = parent.findIndex(item => String(entryId(item)) === key);
      if (server === undefined) { if (index >= 0) parent.splice(index, 1); }
      else if (index >= 0) parent[index] = server;
      else parent.push(server);
    } else if (path.includes('[')) {
      // Arrays are sent as complete collections. Keep the server value of a
      // disputed entry while allowing independent appended entries through.
      if (server === undefined) delete parent[key]; else parent[key] = server;
    } else delete parent[key];
  }
  const prune = value => {
    if (!object(value)) return value;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, prune(item)])
      .filter(([, item]) => !object(item) || Object.keys(item).length));
  };
  return prune(copy);
}
